import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBackRounded, ChatBubbleOutlineRounded, PictureAsPdfRounded } from '@mui/icons-material';
import { getAssignedTopic, startTopicChat, PdfExtraction } from '../services/api';

/**
 * Student-side view of a single assigned topic.
 * Renders the extracted sections read-only, with a "Discuss with AI" button
 * that takes the student to /chat with a seed prompt pre-filled in the message
 * input (handed off via location.state, picked up by ChatInterface).
 */
const AssignedTopic: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [topic, setTopic] = useState<PdfExtraction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!id) return;
    const extractionId = Number(id);
    if (Number.isNaN(extractionId)) {
      setError('Invalid topic id.');
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getAssignedTopic(extractionId)
      .then((row) => {
        if (!cancelled) setTopic(row);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load topic.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const [isStartingChat, setIsStartingChat] = React.useState(false);

  const handleDiscussWithAi = async () => {
    if (!topic) return;
    setError(null);
    setIsStartingChat(true);
    try {
      // Backend creates a NEW conversation with topic_context set so the AI
      // stays anchored to the curated sections for the lifetime of this chat.
      const { conversation_id } = await startTopicChat(topic.id);
      navigate('/chat', { state: { selectConversationId: conversation_id } });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the chat. Please try again.');
    } finally {
      setIsStartingChat(false);
    }
  };

  if (isLoading) {
    return (
      <div className="main-gradient-bg min-h-screen flex items-center justify-center">
        <p className="text-sm text-slate-600">Loading topic…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-gradient-bg min-h-screen flex flex-col items-center justify-center gap-4 px-4">
        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </p>
        <button
          type="button"
          onClick={() => navigate('/chat')}
          className="rounded-xl bg-violet-500 px-4 py-2 text-sm font-medium text-white hover:bg-violet-600"
        >
          Back to chat
        </button>
      </div>
    );
  }

  if (!topic) return null;

  return (
    <div className="main-gradient-bg min-h-screen px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <header className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/chat')}
            className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-violet-700"
          >
            <ArrowBackRounded fontSize="small" /> Back to chat
          </button>
        </header>

        <section className="rounded-3xl border border-violet-200 bg-white/95 p-6 shadow-sm space-y-4">
          <div className="flex items-start gap-3">
            <PictureAsPdfRounded className="text-violet-500" />
            <div className="flex-1">
              <h1 className="text-xl font-bold text-slate-900">{topic.file_name}</h1>
              <p className="text-xs text-slate-500">
                {topic.page_count != null ? `${topic.page_count} pages · ` : ''}
                {topic.sections?.length ?? 0} sections
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleDiscussWithAi}
            disabled={isStartingChat}
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChatBubbleOutlineRounded fontSize="small" />
            {isStartingChat ? 'Starting…' : 'Discuss this with the AI'}
          </button>
        </section>

        {Array.isArray(topic.sections) && topic.sections.length > 0 ? (
          <section className="space-y-3">
            {topic.sections.map((s, idx) => (
              <article
                key={idx}
                className="rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-sm"
              >
                <h2 className="font-semibold text-slate-800">
                  {idx + 1}. {s.title}
                </h2>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{s.content}</p>
              </article>
            ))}
          </section>
        ) : (
          <p className="text-sm text-slate-500">No sections in this topic.</p>
        )}
      </div>
    </div>
  );
};

export default AssignedTopic;
